import type { AgentPlanIssue, AiConsentStatus, RecordTestError, RecordTestStatus } from '@cti/contracts';
import { ApiRequestError } from './api';
import { CONSENT_WORDS } from './call-words';
import { errorText, wordFor } from './outreach-words';

/** Test a record (plan 1E): why a preview failed, in words. */
export const RECORD_TEST_ERROR_WORDS: Record<RecordTestError, string> = {
  not_found: "That record isn't in your Salesforce.",
  not_connected: 'Salesforce is not connected. An admin can connect it in Settings.',
  salesforce_error: "Salesforce didn't answer while the record was read. Try again.",
  plan_failed: "The AI couldn't write a usable plan. Try again.",
  timeout: 'The AI took too long to write the plan. Try again.',
  interrupted: 'The preview stopped part way (the server restarted). Try again.',
  internal_error: 'Something went wrong on our side while writing the preview. Try again.',
};

export const RECORD_TEST_STATUS_WORDS: Record<RecordTestStatus, string> = { running: 'Running', ready: 'Ready', failed: 'Failed' };

/**
 * Why a preview offers no appointment times (outreach-api appointments/offer.ts `OfferNote`). Times are routed by region: the
 * property's county picks the region, and that region's active reps take the times. A test offers times whatever the Book
 * appointments switch says (1D practiceBooking), so `booking_off` here only means no region has a rep. A note no longer
 * written (an older preview's `no_owner`) reads as the plain words.
 */
const OFFER_NOTE_WORDS: Readonly<Record<string, string>> = {
  booking_off: 'No reps are set up for any region, so no times would be offered.',
  no_reps: "No active rep for the property's region, so no regular times would be offered.",
  no_address: 'The record has no property address, so no times would be offered.',
  maps_unavailable: "Couldn't look up the property on the map (Google Maps key missing or Google unavailable), so no times would be offered.",
  not_found: "Google couldn't find the property address, so no times would be offered.",
  outside_regions: 'The property is outside the LA and San Diego regions, so no times would be offered.',
  already_booked: 'The seller already has a consultation booked, so no new times would be offered.',
  no_free_time: 'No free time in the next 15 days, so no times would be offered.',
  salesforce_error: "Couldn't read Salesforce (the calendar, the consultations or the property), so no times would be offered.",
  invalid_slots: "Couldn't read the calendar, so no times would be offered.",
};

export function offerNoteWords(note: string | null): string {
  return note ? wordFor(OFFER_NOTE_WORDS, note, 'No times would be offered.') : 'No times would be offered.';
}

/** A time in the viewer's own zone, e.g. "3:42 PM". */
export function localTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function retryAtOf(details: unknown): string | null {
  if (!details || typeof details !== 'object') return null;
  const at = (details as { retryAt?: unknown }).retryAt;
  return typeof at === 'string' && !Number.isNaN(Date.parse(at)) ? at : null;
}

/** The plan board's consent words, except that this page's button is Regenerate, not "research again". */
export const RECORD_TEST_CONSENT_WORDS: Record<AiConsentStatus, string> = { ...CONSENT_WORDS, unknown: 'AI consent: could not be read — Regenerate' };

/**
 * A record test route's refusal in words. A 429 keeps the server's reason and says when to try again in the viewer's
 * own zone (the server words it in the tenant's zone).
 */
export function recordTestErrorText(error: unknown): string {
  if (error instanceof ApiRequestError && error.status === 429) {
    const at = retryAtOf(error.details);
    if (at) {
      const reason = error.message.replace(/\s*Try again at .*$/, '');
      return `${reason ? `${reason} ` : ''}Try again at ${localTime(at)}.`;
    }
  }
  return errorText(error);
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The Test a record page's search: `?id=<record test id>` opens that test; anything else is dropped. */
export function recordTestSearch(search: Record<string, unknown>): { id?: string } {
  return typeof search.id === 'string' && UUID.test(search.id) ? { id: search.id } : {};
}

/** Why a plan's draft text failed the plan-text check, in a reader's words. */
export const TEXT_ISSUE_WORDS: Readonly<Record<AgentPlanIssue, string>> = {
  money: 'a price or an amount',
  offer: 'offer wording',
  human_claim: 'a claim to be human',
  disclosure_skip: 'skipping the AI disclosure',
  url: 'a web address',
  angle_bracket: 'a < or > sign',
  control_char: 'a hidden or control character',
  disallowed_char: 'an emoji, a symbol or a look-alike letter',
  line_break: 'a line break',
};
